import axios from "axios";
import React, { useState, useEffect } from "react";
import {
  FaBook,
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaAngleDown,
  FaAngleRight,
  FaUserTie,
  FaHandsHelping,
  FaUsers,
  FaMoneyBillWave,
  FaSchool,
  FaNetworkWired,
  FaHome,
  FaFileAlt,
  FaSmile,
  FaVideo,
  FaGift,
  FaComments,
  FaServer,
  FaUserCircle,
  FaListAlt,
  FaSitemap,
  FaPenAlt,
  FaNewspaper,
  FaThLarge,
  FaTachometerAlt
} from "react-icons/fa";
import { config } from "../../config";

const apiUrl = config.apiUrl;

const SidebarItem = ({ 
  icon: Icon, 
  title, 
  isOpen, 
  hasChildren, 
  onClick, 
  isSelected, 
  isSidebarOpen,
  menu,
  setSelectedMenu
}) => {
  const handleClick = () => {
    if (menu) {
      setSelectedMenu(menu);
    }
    onClick();
  };

  return (
    <li
      className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected ? 'bg-blue-100 text-blue-700 font-semibold' : 'hover:bg-blue-50 text-gray-700'}`}
      onClick={handleClick}
    >
      {Icon && <Icon className={`text-lg ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />}
      {isSidebarOpen && (
        <>
          <span className="ml-3">{title}</span>
          {hasChildren && (
            <span className="ml-auto">
              {isOpen ? <FaAngleDown /> : <FaAngleRight />}
            </span>
          )}
        </>
      )}
    </li>
  );
};

const SubItem = ({ 
  title, 
  onClick, 
  isSelected, 
  isSidebarOpen,
  emoji 
}) => {
  return (
    <li
      onClick={onClick}
      className={`flex items-center p-2 pl-8 rounded-lg cursor-pointer text-sm transition-all duration-200
        ${isSelected ? 'bg-blue-50 text-blue-600 font-medium' : 'hover:bg-blue-50 text-gray-600'}`}
    >
      {isSidebarOpen && (
        <>
          {emoji && <span className="mr-2">{emoji}</span>}
          <span>{title}</span>
        </>
      )}
    </li>
  );
};

const Sidebar = ({
  isSidebarOpen,
  selectedMenu,
  setSelectedMenu,
  toggleSidebar,
  navigate,
  isMobile
}) => {
  // State for all collapsible menus
  const [openMenus, setOpenMenus] = useState(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('openMenus');
    return saved ? JSON.parse(saved) : {
      pages: true,
      webinar: true,
      landingPage: true,
      home: false,
      secrets: false,
      webinarInstructors: false,
      socialproof: false,
      webinarCta: false,
      webinarFaq: false,
      form: false,
      memberships: false,
      services: false,
      blog: false,
      category: false,
      courses: false,
      hero: false,
      testimonials: false,
      aboutPage: false,
      contactPage: false,
      instructors: false,
      thank: false,
      live: false,
      offer: false,
      congrates: false,
      chat: false,
      api: false
    };
  });

  // Save open menus to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('openMenus', JSON.stringify(openMenus));
  }, [openMenus]);

  const toggleMenu = (menu) => {
    console.log(`Toggling menu: ${menu}, current state: ${openMenus[menu]}`);
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleMenuClick = (menu) => {
    console.log(`Menu clicked: ${menu}`);
    setSelectedMenu(menu);
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      await axios.post(`${apiUrl}/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.removeItem("token");
      localStorage.removeItem("selectedMenu");
      localStorage.removeItem("openMenus");
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Menu structure data
  const menuSections = [
    {
      title: "Dashboard",
      icon: FaTachometerAlt,
      menu: "dashboard",
    },
    {
      title: "Pages Editor",
      icon: FaPenAlt,
      key: "pages",
      items: [
         {
          title: "Memberships",
          icon: FaUserCircle,
          key: "memberships",
          items: [
            { title: "Create Membership", menu: "membershipCreate", emoji: "📝" },
            { title: "Membership Overview", menu: "membershipDetails", emoji: "🦸‍♂️" }
          ]
        },
        {
          title: "Services",
          icon: FaHandsHelping,
          key: "services",
          items: [
            { title: "Create Service", menu: "serviceCreate", emoji: "📝" },
            { title: "Service Overview", menu: "serviceDetails", emoji: "🛠️" }
          ]
        },
        {
          title: "Blog",
          icon: FaNewspaper,
          key: "blog",
          items: [
            { title: "Create New Blog", menu: "blogCreate", emoji: "📝" },
            { title: "Blog Overview", menu: "blogOverview", emoji: "📚" }
          ]
        },
        {
          title: "Category",
          icon: FaListAlt,
          key: "category",
          items: [
            { title: "Create Category", menu: "categoryCreate", emoji: "📝" },
            { title: "Category Overview", menu: "categoryOverview", emoji: "🏷️" }
          ]
        },
        {
          title: "Courses",
          icon: FaSchool,
          key: "courses",
          items: [
            { title: "Create Course", menu: "courseCreate", emoji: "📘" },
            { title: "Course Overview", menu: "courseDetails", emoji: "📓" }
          ]
        },
        {
          title: "Hero",
          icon: FaThLarge,
          key: "hero",
          items: [
            { title: "Create Hero", menu: "heroCreate", emoji: "📝" },
            { title: "Hero Overview", menu: "heroOverview", emoji: "🖼️" }
          ]
        },
        {
          title: "Testimonials",
          icon: FaUsers,
          key: "testimonials",
          items: [
            { title: "Create Testimonials", menu: "testimonialCreate", emoji: "📝" },
            { title: "Testimonials Overview", menu: "testimonialOverview", emoji: "🌟" }
          ]
        },
        {
          title: "About Page",
          icon: FaSitemap,
          key: "aboutPage",
          items: [
            { title: "Create About Page", menu: "aboutPageCreate", emoji: "📘" },
            { title: "About Page Overview", menu: "aboutPageOverview", emoji: "📕" }
          ]
        },
        {
          title: "Contact Page",
          icon: FaSitemap,
          key: "contactPage",
          items: [
            { title: "Create Contact Page", menu: "contactPageCreate", emoji: "📘" },
            { title: "Contact Page Overview", menu: "contactPageOverview", emoji: "📕" }
          ]
        },
        {
          title: "Instructors",
          icon: FaUserTie,
          key: "instructors",
          items: [
            { title: "Create Instructor", menu: "instructorCreate", emoji: "📝" },
            { title: "Instructor Overview", menu: "instructorOverview", emoji: "👨‍🏫" }
          ]
        }
      ]
    },
    {
      title: "Webinar Editor",
      icon: FaFileAlt,
      key: "webinar",
      items: [
        {
          title: "Landing Page",
          icon: FaHome,
          key: "landingPage",
          items: [
            {
              title: "Home Page",
              icon: FaHome,
              key: "home",
              items: [
                { title: "Create Home", menu: "createHome", emoji: "📝" },
                { title: "Home Overview", menu: "homeOverview", emoji: "🏠" }
              ]
            },
            {
              title: "Attendees",
              icon: FaUsers,
              key: "attendees",
              items: [
                { title: "Create Attende", menu: "createAttende", emoji: "📝" },
                { title: "Attende Overview", menu: "attendeOverview", emoji: "🏠" }
              ]
            },
            {
              title: "Form",
              icon: FaFileAlt,
              key: "form",
              items: [
                { title: "Create Form", menu: "createForm", emoji: "📝" },
                { title: "Form Overview", menu: "formOverview", emoji: "📋" }
              ]
            },
            {
              title: "Secrets",
              icon: FaBook,
              key: "secrets",
              items: [
                { title: "Create Secret", menu: "createSecret", emoji: "📝" },
                { title: "Secret Overview", menu: "secretOverview", emoji: "🏠" }
              ]
            },
            {
              title: "Bonus",
              icon: FaBook,
              key: "bonuses",
              items: [
                { title: "Create Bonus", menu: "createBonus", emoji: "📝" },
                { title: "Bonus Overview", menu: "bonusOverview", emoji: "🏠" }
              ]
            },
            {
              title: "Webinar Instructors",
              icon: FaUserTie,
              key: "webinarInstructors",
              items: [
                { title: "Create Instructor", menu: "createWebinarInstructor", emoji: "📝" },
                { title: "Instructor Overview", menu: "webinarInstructorOverview", emoji: "🏠" }
              ]
            },
            {
              title: "Social Proof",
              icon: FaUsers,
              key: "socialproof",
              items: [
                { title: "Create Social Proof", menu: "createSocialProof", emoji: "📝" },
                { title: "Social Proof Overview", menu: "socialProofOverview", emoji: "🏠" }
              ]
            },
            {
              title: "CTA",
              icon: FaChartBar,
              key: "webinarCta",
              items: [
                { title: "Create CTA", menu: "createWebinarCta", emoji: "📝" },
                { title: "CTA Overview", menu: "webinarCtaOverview", emoji: "🏠" }
              ]
            },
            {
              title: "FAQ",
              icon: FaComments,
              key: "webinarFaq",
              items: [
                { title: "Create FAQ", menu: "createWebinarFaq", emoji: "📝" },
                { title: "FAQ Overview", menu: "webinarFaqOverview", emoji: "🏠" }
              ]
            },
          ]
        },
        {
          title: "ThankYou Page",
          icon: FaSmile,
          key: "thanks",
          items: [
            {
              title: "Essential",
              icon: FaSmile,
              key: "thank",
              items: [
                { title: "Create ThankYou", menu: "createThank", emoji: "📝" },
                { title: "ThankYou Overview", menu: "thankOverview", emoji: "🙏" }
              ]
            },
            {
              title: "Free Things",
              icon: FaBook,
              key: "freeThings",
              items: [
                { title: "Create Free Thing", menu: "createFreeThing", emoji: "📝" },
                { title: "Free Things Overview", menu: "freethingOverview", emoji: "📋" }
              ]
            },
            {
              title: "Finished Bonuses",
              icon: FaBook,
              key: "finishedBonuses",
              items: [
                { title: "Create Finished Bonus", menu: "createFinishBonus", emoji: "📝" },
                { title: "Finished Bonuses Overview", menu: "finishBonusOverview", emoji: "📋" }
              ]
            },
            {
              title: "Community Access",
              icon: FaUsers,
              key: "communityAccess",
              items: [
                { title: "Create Community", menu: "createCommunity", emoji: "📝" },
                { title: "Community Overview", menu: "communityOverview", emoji: "📋" }
              ]
            },
             {
              title: "Joining Instructions",
              icon: FaUsers,
              key: "joiningInstructions",
              items: [
                { title: "Create Instruction", menu: "createJoiningInstruction", emoji: "📝" },
                { title: "Instruction Overview", menu: "joiningInstructionOverview", emoji: "📋" }
              ]
            },
          ]
        },
        {
          title: "Live Page",
          icon: FaVideo,
          key: "live",
          items: [
            { title: "Create Live", menu: "createLive", emoji: "📝" },
            { title: "Live Overview", menu: "liveOverview", emoji: "🎥" }
          ]
        },
        {
          title: "Offer Page",
          icon: FaGift,
          key: "offer",
          items: [
            { title: "Create Offer", menu: "createOffer", emoji: "📝" },
            { title: "Offer Overview", menu: "offerOverview", emoji: "🎁" }
          ]
        },
        {
          title: "Congrats Page",
          icon: FaSmile,
          key: "congrates",
          items: [
            { title: "Create Congrats", menu: "createCongrates", emoji: "📝" },
            { title: "Congrats Overview", menu: "congratesOverview", emoji: "🎉" }
          ]
        },
        {
          title: "Simulator Chats",
          icon: FaComments,
          key: "chat",
          items: [
            { title: "Create Chat", menu: "createChat", emoji: "📝" },
            { title: "Chat Overview", menu: "chatOverview", emoji: "💬" }
          ]
        },
        {
          title: "Brevo API",
          icon: FaServer,
          key: "api",
          items: [
            { title: "Create Brevo API", menu: "createBrevoApi", emoji: "📝" },
            { title: "Brevo API Overview", menu: "brevoApiOverview", emoji: "🔌" }
          ]
        },
        {
          title: "Leads",
          menu: "leadOverview",
          icon: FaUsers
        },
        {
          title: "Transactions",
          menu: "transactionOverview",
          icon: FaMoneyBillWave
        }
      ]
    },
    {
      title: "Pixel Editor",
      icon: FaServer,
      key: "pixels",
      items: [
        { title: "Create Pixel", menu: "createPixel", emoji: "📝" },
        { title: "Pixel Overview", menu: "pixelOverview", emoji: "🔌" }
      ]
    },
    {
      title: "Transactions",
      menu: "transactions",
      icon: FaMoneyBillWave
    },
    {
      title: "Users",
      menu: "users",
      icon: FaNetworkWired
    },
    {
      title: "Help Desk",
      menu: "contact",
      icon: FaHandsHelping
    },
    {
      title: "All Applications",
      menu: "applications",
      icon: FaListAlt
    }
  ];

  return (
    <aside
      className={`bg-white shadow-lg md:shadow-sm fixed md:static z-30 top-0 left-0 h-full w-96 transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      style={{ top: isMobile ? '60px' : '0' }}
    >
      <div className="h-full overflow-y-auto py-4 px-4">
        {isSidebarOpen && (
          <div className="px-3 mb-4">
            <h2 className="text-xl font-bold text-gray-800">Admin Panel</h2>
            <p className="text-xs text-gray-500">Manage your content</p>
          </div>
        )}
        <ul className="space-y-1">
          {menuSections.map((section) => {
            if (section.items) {
              return (
                <div key={section.key || section.menu}>
                  <SidebarItem
                    icon={section.icon}
                    title={section.title}
                    isOpen={openMenus[section.key]}
                    hasChildren={true}
                    onClick={() => toggleMenu(section.key)}
                    isSelected={false}
                    isSidebarOpen={isSidebarOpen}
                    setSelectedMenu={setSelectedMenu}
                  />
                  {openMenus[section.key] && isSidebarOpen && (
                    <ul className="space-y-1 mt-1">
                      {section.items.map((item) => {
                        if (item.items) {
                          return (
                            <div key={item.key || item.menu}>
                              <SidebarItem
                                icon={item.icon}
                                title={item.title}
                                isOpen={openMenus[item.key]}
                                hasChildren={true}
                                onClick={() => toggleMenu(item.key)}
                                isSelected={false}
                                isSidebarOpen={isSidebarOpen}
                                setSelectedMenu={setSelectedMenu}
                              />
                              {openMenus[item.key] && isSidebarOpen && (
                                <ul className="space-y-1 mt-1">
                                  {item.items.map((subItem) => {
                                    if (subItem.items) {
                                      return (
                                        <div key={subItem.key || subItem.menu}>
                                          <SidebarItem
                                            icon={subItem.icon}
                                            title={subItem.title}
                                            isOpen={openMenus[subItem.key]}
                                            hasChildren={true}
                                            onClick={() => toggleMenu(subItem.key)}
                                            isSelected={false}
                                            isSidebarOpen={isSidebarOpen}
                                            setSelectedMenu={setSelectedMenu}
                                          />
                                          {openMenus[subItem.key] && isSidebarOpen && (
                                            <ul className="space-y-1 mt-1">
                                              {subItem.items.map((subSubItem) => (
                                                <SubItem
                                                  key={subSubItem.menu}
                                                  title={subSubItem.title}
                                                  onClick={() => handleMenuClick(subSubItem.menu)}
                                                  isSelected={selectedMenu === subSubItem.menu}
                                                  isSidebarOpen={isSidebarOpen}
                                                  emoji={subSubItem.emoji}
                                                />
                                              ))}
                                            </ul>
                                          )}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <SubItem
                                          key={subItem.menu}
                                          title={subItem.title}
                                          onClick={() => handleMenuClick(subItem.menu)}
                                          isSelected={selectedMenu === subItem.menu}
                                          isSidebarOpen={isSidebarOpen}
                                          emoji={subItem.emoji}
                                        />
                                      );
                                    }
                                  })}
                                </ul>
                              )}
                            </div>
                          );
                        } else {
                          return (
                            <SidebarItem
                              key={item.menu}
                              icon={item.icon}
                              title={item.title}
                              isOpen={false}
                              hasChildren={false}
                              onClick={() => handleMenuClick(item.menu)}
                              isSelected={selectedMenu === item.menu}
                              isSidebarOpen={isSidebarOpen}
                              menu={item.menu}
                              setSelectedMenu={setSelectedMenu}
                            />
                          );
                        }
                      })}
                    </ul>
                  )}
                </div>
              );
            } else {
              return (
                <SidebarItem
                  key={section.menu}
                  icon={section.icon}
                  title={section.title}
                  isOpen={false}
                  hasChildren={false}
                  onClick={() => handleMenuClick(section.menu)}
                  isSelected={selectedMenu === section.menu}
                  isSidebarOpen={isSidebarOpen}
                  menu={section.menu}
                  setSelectedMenu={setSelectedMenu}
                />
              );
            }
          })}
          
          {/* Logout */}
          <SidebarItem
            icon={FaSignOutAlt}
            title="Logout"
            isOpen={false}
            hasChildren={false}
            onClick={handleLogout}
            isSelected={false}
            isSidebarOpen={isSidebarOpen}
          />
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;